import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input, Select, Textarea } from '@wove/web-ui'
import { X } from 'lucide-react'
import {
  CustomFieldMapping,
  CreateCustomFieldMappingRequest,
  UpdateCustomFieldMappingRequest,
} from '@wove/api'

const customFieldMappingSchema = z
  .object({
    key: z
      .string()
      .min(1, 'Field key is required')
      .max(100, 'Field key must be 100 characters or less'),
    dataType: z.enum(['text', 'number', 'boolean', 'date'], {
      required_error: 'Data type is required',
    }),
    value: z.string().optional(),
  })
  .refine(
    (data) => {
      if (!data.value) return true // Optional field

      switch (data.dataType) {
        case 'number':
          return !isNaN(Number(data.value))
        case 'boolean':
          return ['true', 'false', '1', '0'].includes(data.value.toLowerCase())
        case 'date':
          return !isNaN(Date.parse(data.value))
        default:
          return true
      }
    },
    {
      message: 'Value does not match the selected data type',
      path: ['value'],
    },
  )

type FormData = z.infer<typeof customFieldMappingSchema>

interface CustomFieldMappingFormProps {
  mapping?: CustomFieldMapping
  onSubmit: (data: CreateCustomFieldMappingRequest | UpdateCustomFieldMappingRequest) => void
  onCancel: () => void
  isSubmitting: boolean
}

const DATA_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
]

export default function CustomFieldMappingForm({
  mapping,
  onSubmit,
  onCancel,
  isSubmitting,
}: CustomFieldMappingFormProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(customFieldMappingSchema),
    defaultValues: {
      key: mapping?.key || '',
      dataType: mapping?.dataType || 'text',
      value: mapping?.value || '',
    },
  })

  const dataType = watch('dataType')

  const onFormSubmit = (data: FormData) => {
    onSubmit({
      key: data.key.trim(),
      dataType: data.dataType,
      value: data.value || undefined,
    })
  }

  const renderValueInput = () => {
    return (
      <Controller
        name="value"
        control={control}
        render={({ field }) => {
          switch (dataType) {
            case 'boolean':
              return (
                <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                  <option value="">Select value...</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Select>
              )
            case 'date':
              return (
                <Input
                  type="date"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                  className={errors.value ? 'border-red-300' : ''}
                />
              )
            case 'text':
              return (
                <Textarea
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter text value..."
                  rows={3}
                  disabled={isSubmitting}
                  className={errors.value ? 'border-red-300' : ''}
                />
              )
            default:
              return (
                <Input
                  type={dataType === 'number' ? 'number' : 'text'}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={`Enter ${dataType} value...`}
                  disabled={isSubmitting}
                  className={errors.value ? 'border-red-300' : ''}
                />
              )
          }
        }}
      />
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {mapping ? 'Edit Custom Field Mapping' : 'Create Custom Field Mapping'}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field Key *</label>
            <Controller
              name="key"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g., order_priority, customer_reference"
                  className={errors.key ? 'border-red-300' : ''}
                  disabled={isSubmitting || !!mapping}
                />
              )}
            />
            {errors.key && <p className="mt-1 text-sm text-red-600">{errors.key.message}</p>}
            <p className="mt-1 text-sm text-gray-500">A unique identifier for this custom field</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Type *</label>
            <Controller
              name="dataType"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DATA_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            />
            <p className="mt-1 text-sm text-gray-500">The type of data this field will store</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
            {renderValueInput()}
            {errors.value && <p className="mt-1 text-sm text-red-600">{errors.value.message}</p>}
            <p className="mt-1 text-sm text-gray-500">
              The current value for this field (optional)
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mapping ? 'Update Mapping' : 'Create Mapping'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
